import {useQuery} from '@tanstack/react-query'
import {useLocation} from 'react-router-dom'
import {BR_SEARCH_URL, USEQUERY_OPTIONS, SELECTED_ATTRIBUTES_WIDGET_PRODUCTS} from '../constants'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {formatDate} from '../utils/product-utils'
import {useAccessToken, useCommerceApi} from '@salesforce/commerce-sdk-react'
import {CookieStorage} from '@salesforce/commerce-sdk-react/auth/storage'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import {onClient} from '@salesforce/commerce-sdk-react/utils'
import {buildApiUrl, getOrCreateBrUid2} from '../utils/common-utils'

const useBrCategory = (parameters = {}) => {
    const queryParams = USEQUERY_OPTIONS
    const selectedAttributes = SELECTED_ATTRIBUTES_WIDGET_PRODUCTS
    const appOrigin = useAppOrigin()
    const cookie =
        onClient() &&
        new CookieStorage({
            keySuffix: '',
            sharedContext: !onClient()
        })
    const location = useLocation()
    const {
        app: {commerceAPI}
    } = getConfig()

    const {bloomreach, storefront} = commerceAPI || {}
    const {config} = bloomreach || {}
    const {caching, hostUrl} = storefront || {}

    let catalogViews = ''
    let accountId = ''
    let searchBaseUrl = ''
    let searchCacheTime = 2 * 60 * 60 * 1000

    try {
        const configAsJson = config ? JSON.parse(config) : {}
        if (configAsJson) {
            accountId = configAsJson.accountId || ''
            searchBaseUrl = configAsJson.searchBaseUrl || 'core.dxpapi.com'
            catalogViews = configAsJson.catalogViews || ''
        }
    } catch (e) {
        console.log(e.message)
    }

    try {
        const cachingConfig = caching ? JSON.parse(caching) : {}
        searchCacheTime = cachingConfig.search || searchCacheTime
    } catch (e) {
        console.log(e.message)
    }

    const api = useCommerceApi()
    const {getTokenWhenReady} = useAccessToken()

    /** ---- 1. Bloomreach Category Search ---- **/
    const enabledBrQuery = !!parameters?.enableBloomreachSearch && !!parameters?.id
    const brSearchQuery = useQuery({
        queryKey: [
            'br-category-widget',
            parameters?.id,
            parameters?.categoryFilters || 'no-filter'
        ],
        queryFn: async () => {
            const {id, effectiveDateTime, categoryFilters, enableBloomreachSearch} = parameters
            if (!enableBloomreachSearch || !id) return null

            const formattedDate = formatDate(effectiveDateTime)
            const currentDate = formatDate()
            const disableRules = formattedDate > currentDate

            const baseUrl = new URL(
                onClient()
                    ? `${appOrigin}${BR_SEARCH_URL}`
                    : `https://${searchBaseUrl}/api/v1/core/` // SSR should call bloomreach direcly - no need to go back to proxy
            )

            const merchandisingRules = disableRules
                ? {'merchandising_rules.disable': 'attribute_include, attribute_exclude'}
                : {}
            const brUid = getOrCreateBrUid2(cookie)
            const queryParams = {
                start: 0,
                rows: 15,
                account_id: accountId,
                domain_key: catalogViews,
                q: id,
                sort: '',
                request_type: 'search',
                ref_url: `${hostUrl}${location?.pathname}${location?.search}`,
                url: `${hostUrl}${location?.pathname}${location?.search}`,
                fl: 'pid,skuid,product_type',
                search_type: 'category',
                efq: categoryFilters,
                _br_uid_2: brUid,
                ...merchandisingRules
            }

            if (formattedDate && !onClient()) {
                queryParams.fq = `online_date:[* TO ${formattedDate}]`
            }

            const url = buildApiUrl(baseUrl, queryParams)

            const apiResponse = await fetch(url, {
                headers: {
                    formatteddate: formattedDate
                },
                credentials: 'omit'
            })
            const jsonResposne = await apiResponse.json()
            return jsonResposne
        },
        ...queryParams,
        staleTime: searchCacheTime,
        enabled: enabledBrQuery
    })

    /** ---- 2. SCAPI Product Details (dependent) ---- **/
    const productIds = brSearchQuery.data?.response?.docs
        ?.map((d) => (d?.product_type === 'set' ? d.pid : d?.variants?.[0]?.skuid) || d.pid)
        ?.filter(Boolean)
        ?.join(',')
    const enabledGetProducts = !!brSearchQuery.data?.response?.docs?.length && !!productIds
    const productsQuery = useQuery({
        queryKey: [
            'use-scapi-products',
            productIds,
            parameters?.countryISO,
            parameters?.currencyCode,
            parameters?.customerGroupIds || '-'
        ],
        queryFn: async () => {
            if (!productIds) return null

            const token = await getTokenWhenReady()

            return api.shopperProducts.getProducts({
                parameters: {
                    ids: productIds,
                    allImages: true,
                    perPricebook: true,
                    select: selectedAttributes,
                    expand: [
                        'availability',
                        'links',
                        'promotions',
                        'options',
                        'images',
                        'prices',
                        'variations'
                    ],
                    c_selectedCountry: parameters?.countryISO,
                    c_selectedCurrency: parameters?.currencyCode,
                    c_isBloomreachSearch: true
                },
                headers: {Authorization: `Bearer ${token}`}
            })
        },
        enabled: enabledGetProducts,
        ...queryParams
    })
    return {
        data: productsQuery?.data?.total ? productsQuery?.data : [],
        isLoading: brSearchQuery.isLoading || productsQuery.isLoading
    }
}

export default useBrCategory
